import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import { SPELL_REFRESH_EVENT } from './spellRefreshEvents';

const post = async (url, body, fallback) => {
  const response = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || fallback);
  return result;
};
const base = campaignId => `/campaigns/${encodeURIComponent(campaignId)}/countercharm`;

export const openCountercharm = (campaignId, save) => post(base(campaignId), save, 'Countercharm could not be asked for.');
export const decideCountercharm = (campaignId, windowId, body) => post(`${base(campaignId)}/${encodeURIComponent(windowId)}/decide`, body, 'Countercharm could not be saved.');
export const syncCountercharm = (campaignId, body) => post(`${base(campaignId)}/sync`, body, 'Countercharm windows could not be loaded.');

/** How long a failed save waits for a Bard before going ahead, so a player who has left can't hold up the table. */
export const COUNTERCHARM_WAIT_MS = 30000;

/** Resolves the closed window, or null once the wait runs out. Wakes on the shared event and also polls every 2 seconds. */
const waitForAnswer = (campaignId, windowId, waitMs) => new Promise((resolve) => {
  let finished = false;
  const finish = (result) => { if (finished) return; finished = true; clearInterval(timer); clearTimeout(limit); window.removeEventListener(SPELL_REFRESH_EVENT, onEvent); resolve(result); };
  const check = async () => {
    try {
      const current = (await syncCountercharm(campaignId, { windowId })).windows?.[0];
      if (!current || current.status !== 'pending') finish(current || null);
    } catch { /* The next event or tick retries. */ }
  };
  const onEvent = (event) => { if (event.detail?.campaignId === campaignId && event.detail?.fateWindows) check(); };
  const timer = setInterval(check, 2000);
  const limit = setTimeout(() => finish(null), waitMs);
  window.addEventListener(SPELL_REFRESH_EVENT, onEvent);
  check();
});

/**
 * After a failed save the player kept: asks any Bard who can react. The server decides whether this save qualifies, so most
 * saves end at the first request. Resolves `{ countercharm: true }` when a Bard used it, else `{}`; a failure here never
 * stops the save.
 */
export async function askCountercharm({ campaignId, waitMs = COUNTERCHARM_WAIT_MS, ...save }) {
  if (!campaignId) return {};
  try {
    const opened = await openCountercharm(campaignId, save);
    let answer = opened.window;
    if (!answer) return {};
    if (answer.status === 'pending') {
      notify(`Waiting for ${answer.candidates.map(({ name }) => name).join(' or ')} to use Countercharm…`, 'info');
      answer = await waitForAnswer(campaignId, answer.windowId, waitMs)
        || (await decideCountercharm(campaignId, opened.window.windowId, { decision: 'skip' })).window;
    }
    if (answer?.status !== 'used') return {};
    notify(`Countercharm from ${answer.usedBy.name}: rerolling with Advantage.`, 'success');
    return { countercharm: true };
  } catch (error) { notify(error.message, 'warning'); return {}; }
}
